/**
 * Protected Route Component
 *
 * Wrapper for routes that require authentication.
 * Redirects to login if user is not authenticated.
 * Redirects to /accounts if user has no accounts and tries to access account-dependent routes.
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useHasAccounts } from '../hooks/useHasAccounts';

interface ProtectedRouteProps {
  children: ReactNode;
}

// Routes that require accounts to show meaningful content
const ACCOUNT_REQUIRED_ROUTES = [
  '/dashboard',
  '/holdings',
  '/transactions',
  '/income',
  '/gains',
  '/cost-basis',
  '/performance',
  '/rebalancer',
  '/income-estimator',
  '/taxes',
];

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { hasAccounts, isLoading: accountsLoading } = useHasAccounts();
  const location = useLocation();

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading while checking accounts
  if (accountsLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Redirect to /accounts if no accounts and trying to access account-dependent route
  if (!hasAccounts && ACCOUNT_REQUIRED_ROUTES.includes(location.pathname)) {
    return <Navigate to="/accounts" replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
